import React, { Suspense, useEffect, useState } from "react";
import { useParams } from "react-router-dom/cjs/react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { GetEmployeeAppraisals } from "./PMSService";
import ComponentLoader from "../../../Shared/Sidebar/ComponentLoader";

const ViewEmployeeDashboardAppraisal = () => {
  const [showHRComment, setShowHRComment] = useState(false);
  const { guid } = useParams();

  const [appraisal, setAppraisal] = useState(null);
  const [Isloading, setLoading] = useState(false);
  const history = useHistory();

  const toggleHR = () => {
    setShowHRComment((prev) => !prev);
  };

  useEffect(() => {
    if (guid) {
      getAppraisalDetail();
    }
  }, [guid]);

  const getAppraisalDetail = async () => {
    try {
      setLoading(true);

      const response = await GetEmployeeAppraisals({ Guid: guid });

      if (response?.Data) {
        setAppraisal(response.Data);
      }
    } catch (error) {
      console.error("Error fetching appraisal detail:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleKraRatingChange = (kraSnapshotId, rating) => {
    setAppraisal((prev) => ({
      ...prev,
      Kras: prev.Kras.map((kra) =>
        kra.KraSnapshotId === kraSnapshotId
          ? {
              ...kra,
              EffectiveRating: rating,
            }
          : kra,
      ),
    }));
  };

  const handleKraCommentChange = (kraSnapshotId, comment) => {
    setAppraisal((prev) => ({
      ...prev,
      Kras: prev.Kras.map((kra) =>
        kra.KraSnapshotId === kraSnapshotId
          ? {
              ...kra,
              EffectiveComment: comment,
            }
          : kra,
      ),
    }));
  };

  const formatPeriod = (from, to) => {
    if (!from || !to) return "-";

    const fromDate = new Date(from);
    const toDate = new Date(to);

    if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
      return "-";
    }

    return `${fromDate.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    })} – ${toDate.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    })}`;
  };

  const handleGoBack = () => {
    history.push("/Assessment/Employee/Dashboard");
  };

  return (
    <>
      <div className="container-fluid view-appraisal">
        <div className="row g-3 assessment-module">
          <div className="og-main">
            {/* Page Header */}
            <div className="og-page-header">
              <div>
                <div className="og-breadcrumb">
                  <a className="appraisal-breadcrumb-link">My Appraisals</a> ›{" "}
                  {appraisal?.CycleTitle || "-"}
                </div>

                <div className="og-page-title">
                  {appraisal?.CycleTitle || "-"}
                </div>

                <div className="og-page-sub">
                  <span className="text-black fw-bold">Review Period: </span>
                  <span>
                    {formatPeriod(
                      appraisal?.ReviewPeriodFrom,
                      appraisal?.ReviewPeriodTo,
                    )}
                  </span>
                </div>
              </div>

              <div className="appraisal-header-actions">
                <button
                  type="button"
                  className="btn btn-outline btn-sm btn-gray appraisal-back-button"
                  onClick={handleGoBack}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="13"
                    fill="currentColor"
                    className="bi bi-arrow-left"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fillRule="evenodd"
                      d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8"
                    />
                  </svg>
                  Back
                </button>

                {(appraisal?.ComputedScore ||
                  appraisal?.FinalRatingLabelName) && (
                  <span className="badge-og badge-vital appraisal-header-score">
                    {appraisal?.ComputedScore?.toFixed(1)} ·{" "}
                    {appraisal?.FinalRatingLabelName || ""}
                  </span>
                )}
              </div>
            </div>

            {/* Appraisal Summary */}
            <div className="og-card appraisal-summary-card">
              <div className="appraisal-summary-grid">
                {/* Final Score */}
                <div className="summary-item summary-score">
                  <div className="summary-label">Final Score</div>

                  <div className="summary-score-value">
                    {appraisal?.ComputedScore?.toFixed(1) || 0}
                  </div>

                  <div className="summary-subtext">out of 5.0</div>
                </div>

                <div className="summary-divider" />

                {/* Rating Label */}
                <div className="summary-item">
                  <div className="summary-label">Rating Label</div>

                  <div className="summary-value-wrapper">
                    <span className="badge-og badge-vital summary-rating-badge">
                      {appraisal?.FinalRatingLabelName || "-"}
                    </span>
                  </div>
                </div>

                <div className="summary-divider" />

                {/* Promotion */}
                <div className="summary-item">
                  <div className="summary-label">Promotion</div>

                  <div className="summary-value summary-muted">
                    {appraisal?.IsPromotionRecommended
                      ? "Recommended"
                      : "Not Recommended"}
                  </div>
                </div>

                <div className="summary-divider" />

                {/* Acknowledgement */}
                <div className="summary-item">
                  <div className="summary-label">Acknowledgement</div>

                  <div className="summary-value summary-success">
                    {appraisal?.IsAcknowledged && (
                      <i className="fa fa-check-circle" />
                    )}{" "}
                    {appraisal?.IsAcknowledged ? "Acknowledged" : "Pending"}
                  </div>

                  <div className="summary-subtext">
                    {appraisal?.IsAcknowledged
                      ? appraisal?.AcknowledgedOn
                      : "-"}
                  </div>
                </div>

                <div className="summary-divider" />

                {/* First Reviewer */}
                <div className="summary-item">
                  <div className="summary-label">First Reviewer</div>

                  <div className="summary-value summary-muted">
                    {appraisal?.FirstReviewerName || "-"}
                  </div>
                </div>

                <div className="summary-divider" />

                {/* Second Reviewer */}
                <div className="summary-item">
                  <div className="summary-label">Second Reviewer</div>

                  <div className="summary-value summary-muted">
                    {appraisal?.SecondReviewerName || "-"}
                  </div>
                </div>

                <div className="summary-divider" />

                {/* Normalization Actor */}
                <div className="summary-item">
                  <div className="summary-label">Normalization Actor</div>

                  <div className="summary-value summary-muted">
                    {appraisal?.NormalizationActorName || "-"}
                  </div>
                </div>

                {/* Score Breakdown */}
                <div className="score-breakdown appraisal-score-breakdown">
                  {/* KRA Score */}
                  <div className="summary-label">KRA Score</div>

                  <div className="appraisal-score-calculation">
                    <strong>
                      {Number(appraisal?.KraScore || 0).toFixed(1)}
                    </strong>

                    <span>×</span>

                    <span>{Number(appraisal?.KraSectionWeightage || 0)}%</span>

                    <span>=</span>

                    <strong>
                      {(
                        (Number(appraisal?.KraScore || 0) *
                          Number(appraisal?.KraSectionWeightage || 0)) /
                        100
                      ).toFixed(2)}
                    </strong>
                  </div>

                  {/* Open Form / Criteria Score */}
                  <div className="summary-label appraisal-score-label">
                    Open Form Score
                  </div>

                  <div className="appraisal-score-calculation">
                    <strong>
                      {Number(appraisal?.CriteriaScore || 0).toFixed(1)}
                    </strong>

                    <span>×</span>

                    <span>
                      {Number(appraisal?.CriteriaSectionWeightage || 0)}%
                    </span>

                    <span>=</span>

                    <strong>
                      {(
                        (Number(appraisal?.CriteriaScore || 0) *
                          Number(appraisal?.CriteriaSectionWeightage || 0)) /
                        100
                      ).toFixed(2)}
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            {/* Section A: KRA Assessment */}
            <div className="og-card appraisal-kra-section">
              <div className="og-section-hd">
                <div className="og-section-num">A</div>

                <div className="og-section-title">KRA Assessment</div>

                <div className="og-section-wt">
                  {appraisal?.KraSectionWeightage}% of final score
                </div>
              </div>

              {Array.isArray(appraisal?.Kras) &&
                appraisal.Kras.map((kra) => (
                  <div className="og-kra-card" key={kra.KraId || kra.Id}>
                    <div className="og-kra-header appraisal-kra-header">
                      <div>
                        <div className="og-kra-title">
                          {kra.KraTitle || "N/A"}
                        </div>
                      </div>

                      <span className="badge-og badge-adequate appraisal-kra-rating">
                        KRA Rating:{" "}
                        {kra.ManagerRating != null
                          ? `${kra.ManagerRating}/5`
                          : "N/A"}
                      </span>
                    </div>

                    {Array.isArray(kra.Kpis) &&
                      kra.Kpis.map((kpi) => (
                        <div
                          className="og-kpi-item appraisal-kpi-item"
                          key={kpi.KpiSnapshotId}
                        >
                          {/* KPI Title and Completion */}
                          <div className="appraisal-kpi-row">
                            <div className="appraisal-kpi-description">
                              <div className="og-kpi-dot appraisal-kpi-dot" />

                              <span className="appraisal-kpi-title">
                                {kpi.KpiDescription || "N/A"}
                              </span>
                            </div>

                            <div>
                              <span>
                                <b>Completion % : </b>
                              </span>
                              <span className="appraisal-kpi-completion">
                                {kpi.CompletionPct != null
                                  ? `${kpi.CompletionPct}%`
                                  : "0%"}
                              </span>
                            </div>
                          </div>

                          {/* Self Evaluation */}
                          <div className="reviewer-block self">
                            <div
                              className="reviewer-badge"
                              style={{ color: "#5f6b7c" }}
                            >
                              <i className="fa fa-user" /> Your Response
                            </div>

                            <div className="appraisal-reviewer-text appraisal-self-text">
                              {kpi.EmployeeNarrative || "No response provided."}
                            </div>
                          </div>

                          {/* Manager Evaluation */}
                          <div className="reviewer-block manager">
                            <div
                              className="reviewer-badge"
                              style={{ color: "#276749" }}
                            >
                              <i className="fa fa-user-check" />{" "}
                              {kpi.ManagerName
                                ? `Manager — ${kpi.ManagerName}`
                                : "Manager Evaluation"}
                            </div>

                            <div className="appraisal-reviewer-text appraisal-manager-text">
                              {kra.ManagerComment ||
                                "No manager evaluation provided."}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                ))}
            </div>

            {/* Section B: Current Cycle Evaluation */}
            <div className="og-card appraisal-kra-section">
              <div className="og-section-hd">
                <div className="og-section-num">B</div>

                <div className="og-section-title">Current Cycle Evaluation</div>

                <div className="og-section-wt">
                  {Math.round(100 - appraisal?.KraSectionWeightage || 0)}% of
                  final score
                </div>
              </div>

              {Array.isArray(appraisal?.Questions) &&
                appraisal.Questions.map((question) => (
                  <div className="appraisal-question" key={question.QuestionId}>
                    <div className="appraisal-question-header">
                      <span className="appraisal-question-section">
                        {question.SectionName || "N/A"}
                      </span>

                      <span className="appraisal-question-weight">
                        {question.Weightage ?? 0}%
                      </span>
                    </div>

                    <div className="appraisal-question-title">
                      {question.QuestionText || "N/A"}
                    </div>

                    {/* Self Evaluation */}
                    <div className="reviewer-block self">
                      <div
                        className="reviewer-badge"
                        style={{ color: "#5f6b7c" }}
                      >
                        <i className="fa fa-user" /> Your Self Evaluation
                      </div>

                      <p className="appraisal-employee-answer">
                        {question.EmployeeAnswer
                          ? question.EmployeeAnswer
                          : "No self evaluation provided."}
                      </p>
                    </div>

                    {/* Manager Evaluation */}
                    <div className="reviewer-block manager">
                      <div
                        className="reviewer-badge"
                        style={{ color: "#276749" }}
                      >
                        <i className="fa fa-user-check" /> Manager Score:{" "}
                        <strong>
                          {question.EffectiveScore != null
                            ? `${question.EffectiveScore}/5`
                            : "N/A"}
                        </strong>
                      </div>

                      <p className="appraisal-manager-answer">
                        {question.ManagerComment
                          ? question.ManagerComment
                          : "No manager comment provided."}
                      </p>
                    </div>

                    {/* Skip Level */}
                    <div className="reviewer-block skip">
                      <div
                        className="reviewer-badge"
                        style={{ color: "#7c3aed" }}
                      >
                        <i className="fa fa-users" /> Skip-Level Override:
                      </div>

                      <p className="appraisal-skip-answer">
                        {question.SkipLevelComment
                          ? question.SkipLevelComment
                          : "No skip-level comment provided."}
                      </p>
                    </div>
                  </div>
                ))}
            </div>

            {/* Final Comment */}
            <div className="og-card">
              <div className="appraisal-final-comment-header">
                <div className="appraisal-final-comment-title">
                  <i className="fa fa-shield-halved appraisal-final-comment-icon" />{" "}
                  Final Comment
                </div>

                <button
                  onClick={toggleHR}
                  id="hrShowBtn"
                  className="btn-og btn-og-secondary btn-og-sm"
                >
                  <i className="fa fa-eye" /> {showHRComment ? "Hide" : "Show"}
                </button>
              </div>

              {!showHRComment ? (
                <div className="appraisal-comment-hidden">
                  ●●●●●● ●●● ●●●●●●●●● ●●●●●●●●● ●●●●● ●●● ●●●●●●● ●●●●●●●●●●●.
                  ●●●●● ●●●●●●●●●● ●●● ●●● ●●●●●●●●● ●●●●●●●● ●●●● ●●●●● ●●●●●●
                  ●●●●●●●● ●●● ●●●●●●●●● ●●●●●.
                </div>
              ) : (
                <div className="appraisal-comment-visible">
                  {appraisal?.HrComment || "No comment"}
                </div>
              )}

              <div className="appraisal-confidential-note">
                <i className="fa fa-lock" /> This feedback is confidential. Do
                not share with colleagues.
              </div>
            </div>
          </div>
        </div>

        {Isloading ? (
          <Suspense>
            <ComponentLoader />
          </Suspense>
        ) : (
          ""
        )}
      </div>
    </>
  );
};

export default ViewEmployeeDashboardAppraisal;
